"use client";

import { useAppSelector } from "@/store";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/store/store";
import { logout } from "@/store/slices/authSlice";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { cn } from "@/lib/utils/utils";
import UserProfilePanel from "@/app/(features)/user-profile/page";
import { Button } from "./button";
import { Avatar, AvatarFallback } from "./avatar";
import { X } from "lucide-react";
import { useRef } from "react";

interface SidebarUserButtonProps {
  isCollapsed: boolean;
}

export function SidebarUserButton({
  isCollapsed,
}: SidebarUserButtonProps) {
  const { currentUser } = useAppSelector((state: any) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const closeSheetRef = useRef<HTMLButtonElement>(null);

  // No navigation here — logout clears the session state and AuthGuard reacts by
  // replacing the route. Redirecting from both places raced the guard.
  const handleLogout = () => {
    dispatch(logout({}));
  };

  const handleNavigateToProfile = () => {
    closeSheetRef.current?.click();
  };

  if (!currentUser) return null;

  const firstInitial = currentUser.firstName?.[0] ?? "U";
  const lastInitial = currentUser.lastName?.[0] ?? "";
  const fullName = `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "w-full px-3 py-2 cursor-pointer rounded-md hover:bg-transparent hover:text-inherit gap-3",
            isCollapsed ? "justify-center" : "justify-start"
          )}
        >
          <Avatar className="bg-brand/80">
            <AvatarFallback className="text-white font-bold bg-brand/80">
              {firstInitial}
              {lastInitial}
            </AvatarFallback>
          </Avatar>
          {!isCollapsed && <span>{fullName}</span>}
        </Button>
      </SheetTrigger>
      {/* Sidebar panel positioning. The parts that
          matter, none of them cosmetic:
            - translate-x-0! cancels the base slide-in-from-left; pinned at
              left-18 a -100% slide starts behind the rail, so the panel looked
              like it crawled out from inside the sidebar once the backdrop went
            - sm:max-w-none, not max-w-none: the base sets sm:max-w-sm, and
              tailwind-merge keeps both when the variants differ
            - z-55 sits under the z-60 rail, so the rail stays on top */}
      <SheetContent
        side="left"
        showOverlay={false}
        className="fixed left-18! top-0 z-55 flex h-screen w-80 flex-col rounded-none border-r p-0 shadow-xl sm:max-w-none [&]:translate-x-0! transition-opacity"
      >
        <SheetHeader className="p-4 pb-2 border-b">
          <SheetTitle>Profile Overview</SheetTitle>
          <SheetClose ref={closeSheetRef} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
            <X className="h-4 w-4" />
          </SheetClose>
        </SheetHeader>
        <div className="flex-1 min-h-0">
          <UserProfilePanel onLogout={handleLogout} onNavigateToProfile={handleNavigateToProfile} />
        </div>
      </SheetContent>
    </Sheet>
  );
}